/**
 * Theme state (light/dark) persisted to localStorage and reflected on
 * <html class="dark">. Dark is the default for the glassmorphism aesthetic.
 */
import { create } from 'zustand';

type Theme = 'light' | 'dark';

const STORAGE_KEY = 'theme';

function getInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'dark';
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved === 'light' || saved === 'dark') return saved;
  // Default to DARK unconditionally (ignoring OS preference). A dark neutral
  // canvas is the photo-tool standard (Lightroom, Apple Photos fullscreen) —
  // photos pop against it. Light is available only via an explicit toggle.
  return 'dark';
}

function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', theme === 'dark');
  localStorage.setItem(STORAGE_KEY, theme);
}

interface ThemeState {
  theme: Theme;
  isDark: boolean;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const initial = getInitialTheme();

export const useThemeStore = create<ThemeState>((set, get) => ({
  theme: initial,
  isDark: initial === 'dark',
  toggleTheme: () => {
    const next: Theme = get().theme === 'dark' ? 'light' : 'dark';
    applyTheme(next);
    set({ theme: next, isDark: next === 'dark' });
  },
  setTheme: (theme) => {
    applyTheme(theme);
    set({ theme, isDark: theme === 'dark' });
  },
}));

/** Apply the persisted theme once at startup (call before first paint). */
export function initTheme(): void {
  applyTheme(useThemeStore.getState().theme);
}
